import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { apiFetch } from '../services/api';
import { isPrerendering } from '../prerenderData';
import './UnsubscribePage.css';

// The page an unsubscribe link lands on.
//
// Two steps, not one. A single-click link that unsubscribes on page load would
// fire on any mail client that pre-fetches links, which is how people end up
// off a list they never chose to leave. So the link identifies the address, the
// page shows it, and the person presses the button.
//
// The address is read from the signed token by the server - this page never
// accepts an address from the URL, so the form cannot be pointed at somebody
// else.

function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  // 'checking' is what the pre-render captures, so the first client render
  // matches the pre-rendered HTML and hydration adopts it.
  const [state, setState] = useState('checking');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isPrerendering) return;
    if (!token) { setState('bad'); setError('This link is missing its code.'); return; }
    apiFetch(`/api/newsletter/unsubscribe?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { setState('bad'); setError(data.error || 'This link is not valid.'); return; }
        setEmail(data.email);
        setState(data.already ? 'done' : 'ready');
      })
      .catch(() => { setState('bad'); setError('We could not reach the server. Please try again.'); });
  }, [token]);

  const confirm = async () => {
    setState('working');
    try {
      const r = await apiFetch(`/api/newsletter/unsubscribe?t=${encodeURIComponent(token)}`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not unsubscribe.');
      setState('done');
    } catch (e) {
      setState('bad');
      setError(e.message);
    }
  };

  return (
    <div className="unsub-page">
      <SEO
        title="Unsubscribe"
        description="Stop receiving marketing email from ASG Architectural Shading. Order and delivery updates about a purchase are sent separately and are not affected."
        canonical="/unsubscribe"
        noindex
      />
      <div className="unsub-card">
        <p className="unsub-eyebrow">ASG Architectural Shading</p>

        {state === 'checking' && (
          <>
            <h1 className="unsub-title">Checking your link</h1>
            <p className="unsub-text">One moment.</p>
          </>
        )}

        {state === 'ready' && (
          <>
            <h1 className="unsub-title">Unsubscribe</h1>
            <p className="unsub-text">
              Stop sending marketing email to <strong className="unsub-email">{email}</strong>?
            </p>
            <button type="button" className="unsub-btn" onClick={confirm}>Yes, unsubscribe me</button>
            <p className="unsub-note">
              You will still get emails about an order you have placed, such as a receipt or a delivery
              update. Those are not marketing and are sent separately.
            </p>
          </>
        )}

        {state === 'working' && (
          <>
            <h1 className="unsub-title">Unsubscribing</h1>
            <p className="unsub-text">One moment.</p>
          </>
        )}

        {state === 'done' && (
          <>
            <h1 className="unsub-title">Done</h1>
            <p className="unsub-text">
              {email ? <><strong className="unsub-email">{email}</strong> has been removed.</> : 'You have been removed.'}
              {' '}You will not receive marketing email from us again.
            </p>
            <p className="unsub-note">
              Changed your mind? Signing up again on the site puts you straight back on the list.
            </p>
            <p className="unsub-links"><Link to="/">Back to asgshading.com.au</Link></p>
          </>
        )}

        {state === 'bad' && (
          <>
            <h1 className="unsub-title">We could not use that link</h1>
            <p className="unsub-text">{error}</p>
            <p className="unsub-note">
              Email <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a> or call 0422 439 499
              and we will take you off the list by hand.
            </p>
            <p className="unsub-links"><Link to="/">Back to asgshading.com.au</Link></p>
          </>
        )}
      </div>
    </div>
  );
}

export default UnsubscribePage;
